import {
  FOW_BASE_VISIBILITY_RADIUS,
  FOW_VISIBILITY_PER_SEGMENT,
  FOW_MIN_RADIUS,
  FOW_MAX_RADIUS,
  FOG_OF_WAR,
} from '@wooorms/shared';

/** Danger Sense thresholds - small worms can detect large worms at extended range */
const SMALL_WORM_THRESHOLD = FOG_OF_WAR.DANGER_SENSE.SMALL_WORM_SEGMENTS; // < 20 segments
const BIG_WORM_THRESHOLD = FOG_OF_WAR.DANGER_SENSE.BIG_WORM_SEGMENTS; // 50+ segments
const DANGER_SENSE_MULTIPLIER = FOG_OF_WAR.DANGER_SENSE.DETECTION_MULTIPLIER; // 1.5x range

/**
 * Fog of War visibility system.
 * Implements size-based visibility where larger worms can see further.
 * This balances gameplay by giving bigger worms (who are larger targets)
 * more awareness of their surroundings.
 *
 * Supports Vision powerup which increases visibility range.
 *
 * Also implements "Danger Sense" - small worms (<20 segments) can detect
 * large worms (50+ segments) at 1.5x normal range. "The ground shakes when giants approach."
 */
export class FogOfWar {
  /** Current player segment count */
  private playerSegmentCount: number = 0;

  /** Calculated visibility radius based on player size */
  private visibilityRadius: number = FOW_BASE_VISIBILITY_RADIUS;

  /** Player's current position for visibility checks */
  private playerX: number = 0;
  private playerY: number = 0;

  /** Whether Vision powerup is active (1.5x visibility) */
  private visionPowerupActive: boolean = false;

  /** Current sprint duration in seconds (for sprint visibility bonus) */
  private sprintDuration: number = 0;

  /** Visibility preset multiplier (from My World settings) */
  private presetMultiplier: number = 1.0;

  /** Whether fog is completely disabled (full map visibility) */
  private fogDisabled: boolean = false;

  /**
   * Update the player's size (segment count) and recalculate visibility radius.
   * @param segmentCount Number of body segments the player has
   */
  updatePlayerSize(segmentCount: number): void {
    this.playerSegmentCount = segmentCount;
    this.visibilityRadius = this.calculateVisibilityRadius(segmentCount);
  }

  /**
   * Set whether the Vision powerup is active.
   * @param active True if Vision powerup is active
   */
  setVisionPowerupActive(active: boolean): void {
    if (this.visionPowerupActive !== active) {
      this.visionPowerupActive = active;
      // Recalculate visibility when powerup state changes
      this.visibilityRadius = this.calculateVisibilityRadius(this.playerSegmentCount);
    }
  }

  /**
   * Set the current sprint duration for visibility bonus calculation.
   * Sustained sprinting grants visibility bonuses:
   * - 0-20 sec: No bonus
   * - 20-30 sec: +10% visibility
   * - 30+ sec: +20% visibility
   * @param duration Sprint duration in seconds
   */
  setSprintDuration(duration: number): void {
    if (this.sprintDuration !== duration) {
      this.sprintDuration = duration;
      // Recalculate visibility when sprint duration changes
      this.visibilityRadius = this.calculateVisibilityRadius(this.playerSegmentCount);
    }
  }

  /**
   * Get the current sprint duration.
   * @returns Sprint duration in seconds
   */
  getSprintDuration(): number {
    return this.sprintDuration;
  }

  /**
   * Check if Vision powerup is active.
   */
  isVisionPowerupActive(): boolean {
    return this.visionPowerupActive;
  }

  /**
   * Set visibility preset multiplier (from My World settings).
   * @param multiplier Multiplier applied to base visibility (1.0 = normal)
   * @param disabled Whether fog is completely disabled
   */
  setVisibilityPreset(multiplier: number, disabled: boolean): void {
    this.presetMultiplier = multiplier;
    this.fogDisabled = disabled;
    // Recalculate visibility with new preset
    this.visibilityRadius = this.calculateVisibilityRadius(this.playerSegmentCount);
  }

  /**
   * Check if fog of war is disabled (full map visibility).
   */
  isFogDisabled(): boolean {
    return this.fogDisabled;
  }

  /**
   * Update the player's position for visibility calculations.
   * @param x Player's world X coordinate
   * @param y Player's world Y coordinate
   */
  updatePlayerPosition(x: number, y: number): void {
    this.playerX = x;
    this.playerY = y;
  }

  /**
   * Calculate visibility radius based on segment count.
   * Formula: BASE + (segments * PER_SEGMENT), clamped to MIN/MAX
   * Applies preset multiplier, Vision powerup, and sprint bonuses.
   * @param segmentCount Number of body segments
   * @returns Visibility radius in world units
   */
  private calculateVisibilityRadius(segmentCount: number): number {
    // If fog is disabled, return a very large radius
    if (this.fogDisabled) {
      return FOW_MAX_RADIUS * 10; // Effectively infinite
    }

    const baseRadius = FOW_BASE_VISIBILITY_RADIUS;
    const bonusRadius = segmentCount * FOW_VISIBILITY_PER_SEGMENT;
    let totalRadius = baseRadius + bonusRadius;

    // Apply preset multiplier (from My World settings)
    totalRadius *= this.presetMultiplier;

    // Apply sprint visibility bonus based on sustained sprint duration
    const sprintBonus = FOG_OF_WAR.SPRINT_VISIBILITY_BONUS;
    if (this.sprintDuration >= sprintBonus.TIER_2_SECONDS) {
      // 30+ seconds: +20% visibility
      totalRadius *= 1 + sprintBonus.TIER_2_BONUS;
    } else if (this.sprintDuration >= sprintBonus.TIER_1_SECONDS) {
      // 20-30 seconds: +10% visibility
      totalRadius *= 1 + sprintBonus.TIER_1_BONUS;
    }

    // Apply Vision powerup multiplier (1.5x)
    if (this.visionPowerupActive) {
      totalRadius *= FOG_OF_WAR.VISION_POWERUP_MULTIPLIER;
    }

    return Math.max(FOW_MIN_RADIUS, Math.min(FOW_MAX_RADIUS, totalRadius));
  }

  /**
   * Check if an entity at the given position is visible to the player.
   * Uses squared distance for performance (avoids sqrt).
   * @param entityX Entity's world X coordinate
   * @param entityY Entity's world Y coordinate
   * @param entityRadius Optional radius of the entity (for partial visibility)
   * @returns True if the entity is within visibility range
   */
  isEntityVisible(entityX: number, entityY: number, entityRadius: number = 0): boolean {
    const dx = entityX - this.playerX;
    const dy = entityY - this.playerY;
    const distanceSquared = dx * dx + dy * dy;

    // Add entity radius to visibility check so entities partially in range are visible
    const effectiveRadius = this.visibilityRadius + entityRadius;
    const radiusSquared = effectiveRadius * effectiveRadius;

    return distanceSquared <= radiusSquared;
  }

  /**
   * Check if another worm is visible to the player, with "Danger Sense" support.
   * Small worms (under 20 segments) can detect large worms (50+ segments)
   * at 1.5x normal detection range. This is a one-way bonus - large worms
   * don't get extra visibility of small worms.
   *
   * "The ground shakes when giants approach."
   *
   * @param wormX Target worm's world X coordinate (head position)
   * @param wormY Target worm's world Y coordinate (head position)
   * @param wormRadius Target worm's collision radius
   * @param wormSegmentCount Target worm's segment count (for danger sense check)
   * @returns True if the worm is within visibility range
   */
  isWormVisible(
    wormX: number,
    wormY: number,
    wormRadius: number,
    wormSegmentCount: number
  ): boolean {
    const dx = wormX - this.playerX;
    const dy = wormY - this.playerY;
    const distanceSquared = dx * dx + dy * dy;

    // Start with base visibility radius
    let effectiveRadius = this.visibilityRadius + wormRadius;

    // Apply danger sense: small worms detect large worms at extended range
    // This is one-way - only benefits the small worm detecting the large one
    const playerIsSmall = this.playerSegmentCount < SMALL_WORM_THRESHOLD;
    const targetIsBig = wormSegmentCount >= BIG_WORM_THRESHOLD;

    if (playerIsSmall && targetIsBig) {
      effectiveRadius *= DANGER_SENSE_MULTIPLIER;
    }

    const radiusSquared = effectiveRadius * effectiveRadius;
    return distanceSquared <= radiusSquared;
  }

  /**
   * Check if a point is visible using explicit player coordinates.
   * Useful when checking visibility from a specific position.
   * @param playerX Player's X coordinate
   * @param playerY Player's Y coordinate
   * @param entityX Entity's X coordinate
   * @param entityY Entity's Y coordinate
   * @param entityRadius Optional entity radius
   * @returns True if visible
   */
  isPointVisibleFrom(
    playerX: number,
    playerY: number,
    entityX: number,
    entityY: number,
    entityRadius: number = 0
  ): boolean {
    const dx = entityX - playerX;
    const dy = entityY - playerY;
    const distanceSquared = dx * dx + dy * dy;

    const effectiveRadius = this.visibilityRadius + entityRadius;
    const radiusSquared = effectiveRadius * effectiveRadius;

    return distanceSquared <= radiusSquared;
  }

  /**
   * Get the current visibility radius.
   * @returns Visibility radius in world units
   */
  getVisibilityRadius(): number {
    return this.visibilityRadius;
  }

  /**
   * Get the player's current segment count.
   * @returns Number of segments
   */
  getPlayerSegmentCount(): number {
    return this.playerSegmentCount;
  }

  /**
   * Get the player's current position.
   * @returns Object with x and y coordinates
   */
  getPlayerPosition(): { x: number; y: number } {
    return { x: this.playerX, y: this.playerY };
  }

  /**
   * Calculate the alpha (opacity) for an entity based on distance.
   * Entities at the edge of visibility fade out smoothly.
   * @param entityX Entity's X coordinate
   * @param entityY Entity's Y coordinate
   * @param fadeStartPercent Where to start fading (0.8 = 80% of visibility radius)
   * @returns Alpha value from 0 to 1
   */
  getVisibilityAlpha(
    entityX: number,
    entityY: number,
    fadeStartPercent: number = 0.85
  ): number {
    const dx = entityX - this.playerX;
    const dy = entityY - this.playerY;
    const distance = Math.sqrt(dx * dx + dy * dy);

    const fadeStartDistance = this.visibilityRadius * fadeStartPercent;

    if (distance <= fadeStartDistance) {
      return 1.0;
    }

    if (distance >= this.visibilityRadius) {
      return 0.0;
    }

    // Linear fade from fadeStart to visibilityRadius
    const fadeRange = this.visibilityRadius - fadeStartDistance;
    const distanceIntoFade = distance - fadeStartDistance;
    return 1.0 - distanceIntoFade / fadeRange;
  }
}
